import CrudList, { TableColumns, TableFilters } from "../shared/crud/list";
import { TagAssociation } from "./domain/TagAssociation";
import { mapRenderer, StringRenderer } from "../shared/renderers/table";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";
import { Renderer, RendererProps } from "../shared/renderers";
import { Tag, TAG_APPLIES_TO_TYPES, TagAppliesToType } from "./domain/Tag";
import {
  DefaultTableRowActionsProps,
  Edit,
  StyledReactTooltip,
} from "../shared/actions/tableActions";
import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import { useLocaleProvider } from "../../components/locale-provider";
import { LOCALE_FULL } from "../witadmin/Common/constants";
import { listMessages, messages } from "./TagsList.messages";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useProperty } from "../../components/property-provider";
import { Link } from "react-router-dom";
import { Translation } from "./domain/Translation";
import { useTagsContext } from "./TagContext";
import { TagUpdateModal } from "./TagUpdateModal";
import { inputFilter, selectorFilter } from "../shared/crud/filter-widgets";
import { FullWidthLoader } from "../../components/full-width-loader";

const TagsRenderer: Renderer<Tag[]> = ({ value }: RendererProps<Tag[]>) => {
  const { currentLocale } = useLocaleProvider();
  return <>{value && value.map(t => t.getTitle(currentLocale)).join(", ")}</>;
};

const TranslationRenderer = mapRenderer<Translation[], string>(StringRenderer, translations => {
  const { currentLocale } = useLocaleProvider();
  const shortLocale = currentLocale === LOCALE_FULL.es ? "es" : "en";
  return translations.find(t => t.locale === shortLocale)?.translation;
});

const TypeRenderer = mapRenderer<TagAppliesToType, string>(StringRenderer, (type, intl) =>
  translateTagAssociationType(type, intl)
);

const columns: TableColumns<TagAssociation> = {
  ticker: {
    renderer: StringRenderer,
  },
  translations: {
    renderer: TranslationRenderer,
  },
  type: {
    renderer: TypeRenderer,
  },
  tags: {
    renderer: TagsRenderer,
  },
};

const defaultPageSize = 50;
const pageSizes = [50, 100, 500];

export function TagsList() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagsAssociations, setTagsAssociations] = useState<TagAssociation[]>([]);
  const [tagAssociation, setTagAssociation] = useState<TagAssociation | undefined>();
  const [showUpdate, setShowUpdate] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const intl = useIntl();
  const { ticker: propertyTicker } = useProperty();
  const { getTags, getTagsAssociations, updateTagAssociation } = useTagsContext();
  const { currentLocale } = useLocaleProvider();
  const shortLocale = useMemo(
    () => (currentLocale.includes("-") ? currentLocale.split("-")[0] : currentLocale),
    [currentLocale]
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [tags, tagsAssociations] = await Promise.all([
        getTags(),
        getTagsAssociations(propertyTicker),
      ]);
      setTags(tags);
      setTagsAssociations(tagsAssociations);
      setLoading(false);
    })();
  }, [propertyTicker, getTags, getTagsAssociations]);

  const onGetAll = useCallback(
    async ({
      currentPage,
      pageSize,
      setTotalRowsCount,
    }: {
      currentPage: number;
      pageSize: number;
      setTotalRowsCount: Dispatch<SetStateAction<number>>;
    }) => {
      const page = tagsAssociations.slice(
        currentPage * pageSize,
        currentPage * pageSize + pageSize
      );
      setTotalRowsCount(tagsAssociations.length);
      return page;
    },
    [tagsAssociations]
  );

  function onEdit(tagAssociation: TagAssociation) {
    setShowUpdate(true);
    setTagAssociation(tagAssociation);
  }

  async function onSaveTagAssociation(newTagAssociation: TagAssociation) {
    try {
      setShowUpdate(false);
      setTagsAssociations([]);
      await updateTagAssociation(tagAssociation!, newTagAssociation);
    } catch (e) {
      console.error("Error updating tag association", e);
    } finally {
      setTagsAssociations(await getTagsAssociations(propertyTicker));
    }
  }

  const RowActions = useMemo(
    () => ({ row, rest }: DefaultTableRowActionsProps<TagAssociation>) => {
      return (
        <Table.Cell {...rest} style={{ zIndex: 999 }}>
          <StyledReactTooltip id="edit" effect="solid">
            <FormattedMessage {...messages.edit} />
          </StyledReactTooltip>
          <Edit row={row} onGoToEdit={() => onEdit(row)} />
        </Table.Cell>
      );
    },
    []
  );

  const tableFilters: TableFilters<any, any> = useMemo(
    () => ({
      ticker: {
        filterFn: (ticker: string, filter: string) =>
          ticker.toLowerCase().includes(filter.toLowerCase()),
        widget: inputFilter,
      },
      translations: {
        filterFn: (translations: Translation[], filter: string) => {
          const translation = translations.find(t => t.locale === shortLocale)?.translation ?? "";
          return translation.toLowerCase().includes(filter.toLowerCase());
        },
        widget: inputFilter,
      },
      type: {
        filterFn: (type: TagAppliesToType, filter: string) => type === filter,
        widget: selectorFilter(
          <FormattedMessage {...listMessages.tableHeaders?.type} />,
          Object.values(TAG_APPLIES_TO_TYPES).map(value => ({
            value: value,
            label: translateTagAssociationType(value, intl),
          }))
        ),
      },
      tags: {
        filterFn: (tags: Tag[], filter: string) => {
          const tagsTranslations = tags
            .map(t => t.getTitle(currentLocale))
            .join(", ")
            .toLowerCase();
          return tagsTranslations.includes(filter.toLowerCase());
        },
        widget: inputFilter,
      },
    }),
    [currentLocale, shortLocale, intl]
  );

  if (isLoading) {
    return <FullWidthLoader />;
  }

  return (
    <>
      <CrudList
        columns={columns}
        hideExportButton={true}
        messages={listMessages}
        onGetAll={onGetAll}
        filters={tableFilters}
        actions={TagsActions}
        fixedActions={true}
        rowActions={RowActions}
        paging
        pageSizes={pageSizes}
        defaultPageSize={defaultPageSize}
      />
      {showUpdate && tagAssociation && (
        <TagUpdateModal
          show={showUpdate}
          tags={tags}
          tagAssociation={tagAssociation}
          handleSave={onSaveTagAssociation}
          handleClose={() => setShowUpdate(false)}
        />
      )}
    </>
  );
}

function TagsActions() {
  return (
    <Link className="btn btn-primary" to="tags-multi-update">
      <FormattedMessage id={messages.createTagsBtn.id} />
    </Link>
  );
}

function translateTagAssociationType(type: TagAppliesToType, intl: IntlShape) {
  switch (type) {
    case "property":
      return intl.formatMessage(messages.typeProperty);
    case "accommodation":
      return intl.formatMessage(messages.typeAccommodation);
    case "package":
      return intl.formatMessage(messages.typePackage);
    case "extra":
      return intl.formatMessage(messages.typeExtra);
    default:
      return "";
  }
}
